import React, { ReactNode, useEffect, useState } from "react";
import * as FaIcons from "react-icons/fa";
import { MenuItem } from "../../types";

interface MenuSectionProps {
    titulo: string;
    items: MenuItem[];
    color: string;
    borderColor: string;
    icon: ReactNode;
    hover: string;
    press: string;
}

const MenuSection: React.FC<MenuSectionProps> = ({
    titulo,
    items,
    color,
    borderColor,
    icon,
    hover,
    press,
}) => {
    const [icons, setIcons] = useState<
        Record<string, { Component?: React.FC<React.SVGProps<SVGSVGElement>>; src?: string }>
    >({});

    useEffect(() => {
        (async () => {
            const loadedIcons: Record<
                string,
                { Component?: React.FC<React.SVGProps<SVGSVGElement>>; src?: string }
            > = {};

            for (const item of items) {
                try {
                    const iconModule = await import(
                        /* @vite-ignore */
                        `../../assets/icons/fontawesome/svgs/regular/${item.icone}`
                    );

                    if (iconModule.ReactComponent) {
                        loadedIcons[item.icone] = { Component: iconModule.ReactComponent };
                    } else {
                        loadedIcons[item.icone] = { src: iconModule.default };
                    }
                } catch (err) {
                    console.warn(`❌ Ícone não encontrado: ${item.icone}`);
                }
            }

            setIcons(loadedIcons);
        })();
    }, [items]);

    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [pressedIndex, setPressedIndex] = useState<number | null>(null);

    const getHueFromColor = (hex: string): string => {
        switch (hex.toUpperCase()) {
            case "#ED1C24":
                return "350deg";
            case "#4190C1":
                return "190deg";
            case "#009535":
                return "100deg";
            default:
                return "315deg";
        }
    };

    const iconFilterByColor = (hex: string) => {
        switch (hex.toUpperCase()) {
            case "#ED1C24":
                return "invert(14%) sepia(88%) saturate(6500%) hue-rotate(350deg)";
            case "#4190C1":
                return "invert(39%) sepia(85%) saturate(2800%) hue-rotate(190deg)";
            case "#009535":
                return "invert(42%) sepia(75%) saturate(2200%) hue-rotate(100deg)";
            default:
                return "invert(36%)";
        }
    };


    return (
        <section style={{ ...styles.section, borderColor }}>
            <h3 style={{ ...styles.title, backgroundColor: color }}>
                {titulo}
                {icon}
            </h3>

            <div style={styles.grid}>
                {items.map((item, index) => {
                    const IconInfo = icons[item.icone];
                    const IconFallback = (FaIcons as any)[item.icone] || FaIcons.FaRegFileAlt;

                    const isHovered = hoveredIndex === index;
                    const isPressed = pressedIndex === index;

                    const backgroundColor = isPressed ? press : isHovered ? hover : "#fff";

                    return (
                        <a
                            key={index}
                            href={item.href}
                            onMouseEnter={() => setHoveredIndex(index)}
                            onMouseLeave={() => {
                                if (hoveredIndex === index) setHoveredIndex(null);
                                if (pressedIndex === index) setPressedIndex(null);
                            }}
                            onMouseDown={() => setPressedIndex(index)}
                            onMouseUp={() => setPressedIndex(null)}
                            style={{
                                ...styles.card,
                                border: `1px solid ${borderColor}`,
                                backgroundColor,
                                transform: isHovered ? "translateY(-3px)" : "translateY(0)",
                                boxShadow: isHovered
                                    ? "0 3px 8px rgba(0, 0, 0, 0.15)"
                                    : "0 1px 3px rgba(0, 0, 0, 0.08)",
                                transition: "transform 0.2s, box-shadow 0.2s, background-color 0.2s",
                            }}
                        >
                            <div style={styles.icon}>
                                {IconInfo?.Component ? (
                                    <IconInfo.Component
                                        width={28}
                                        height={28}
                                        fill={isHovered || isPressed ? color : "#605859"}
                                        style={{ transition: "fill 0.2s" }}
                                    />
                                ) : IconInfo?.src ? (
                                    // <img
                                    //     src={IconInfo.src}
                                    //     alt={item.nome}
                                    //     width={24}
                                    //     height={24}
                                    //     style={{
                                    //         filter: isHovered || isPressed
                                    //             ? `brightness(0) saturate(100%) invert(43%) sepia(96%) saturate(456%) hue-rotate(${getHueFromColor(
                                    //                 color
                                    //             )}) brightness(1) contrast(1)`
                                    //             : "brightness(0) saturate(100%) invert(36%) sepia(10%) saturate(300%) hue-rotate(315deg)",
                                    //         transition: "filter 0.2s",
                                    //     }}
                                    // />
                                    <img
                                        src={IconInfo.src}
                                        alt={item.nome}
                                        width={24}
                                        height={24}
                                        style={{
                                            filter: isHovered || isPressed
                                                ? iconFilterByColor(color)
                                                : "brightness(0) saturate(100%) invert(36%)",

                                            transition: "filter 0.2s",
                                        }}
                                    />

                                ) : (
                                    <IconFallback
                                        size={28}
                                        color={isHovered || isPressed ? color : "#605859"}
                                        style={{ transition: "color 0.2s" }}
                                    />
                                )}
                            </div>

                            <span
                                style={{
                                    color: isHovered || isPressed ? color : "#333",
                                    transition: "color 0.2s",
                                }}
                            >
                                {item.nome}
                            </span>
                        </a>
                    );
                })}
            </div>
        </section>
    );
};

const styles: Record<string, React.CSSProperties> = {
    section: {
        borderRadius: "16px",
        marginBottom: "20px",
        backgroundColor: "#fff",
        boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
    },
    title: {
        padding: "10px 15px",
        color: "white",
        borderTopLeftRadius: "16px",
        borderTopRightRadius: "16px",
        fontWeight: "bold",
        flexDirection: "row",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    grid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: "15px",
        padding: "20px",
        justifyItems: "center",
        maxWidth: "1000px",
        margin: "0 auto",
    },
    card: {
        border: "1px solid #eee",
        borderRadius: "16px",
        padding: "15px",
        textAlign: "center",
        textDecoration: "none",
        color: "#333",
        width: "100%",
        maxWidth: "240px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
    },
    icon: {
        fontSize: "24px",
        marginBottom: "8px",
    },
};

export default MenuSection;

